import React from "react";
import { motion } from "framer-motion";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, YAxis } from "recharts";
import { cn } from "@/lib/utils";

function formatValue(v, unit) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  switch (unit) {
    case "currency":
      return new Intl.NumberFormat("en-IN", {
        style: "currency", currency: "INR", maximumFractionDigits: 0,
      }).format(v);
    case "percent":
      return `${v.toFixed(1)}%`;
    case "count":
      return new Intl.NumberFormat("en-IN").format(Math.round(v));
    case "ratio":
      return v.toFixed(2);
    case "hours":
      return `${v.toFixed(0)} hrs`;
    default:
      return v.toLocaleString();
  }
}

function deltaColor(delta, direction_good) {
  if (delta === null || delta === undefined) return "text-stone-500";
  if (delta === 0) return "text-stone-500";
  const isUp = delta > 0;
  const isGood = (isUp && direction_good === "up") || (!isUp && direction_good === "down");
  return isGood ? "text-brand-good" : "text-brand-bad";
}

export default function KPICard({ metric, index = 0 }) {
  const { name, current, prior, delta_abs, delta_pct, trend, unit, trend_direction_good } = metric;
  // keep missing values as gaps (e.g. months with no membership snapshot)
  const trendData = (trend || []).map((t) => ({ month: t.month, value: t.value ?? null }));
  const color = deltaColor(delta_abs, trend_direction_good);
  const isUp = (delta_abs ?? 0) > 0;
  const isFlat = (delta_abs ?? 0) === 0;
  const Icon = isFlat ? Minus : isUp ? ArrowUpRight : ArrowDownRight;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04, duration: 0.35 }}
      className="group h-full"
    >
      <div
        data-testid={`kpi-card-${metric.metric_id}`}
        className={cn(
          "h-full rounded-lg bg-white border border-stone-200 p-6",
          "shadow-[0_4px_20px_rgba(60,74,62,0.03)]",
          "hover:shadow-[0_10px_30px_rgba(60,74,62,0.08)] hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200"
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500 leading-relaxed">
              {name}
            </div>
            {metric.period_note && (
              <div className="text-[10px] text-brand mt-0.5" data-testid={`kpi-period-note-${metric.metric_id}`}>
                {metric.period_note}
              </div>
            )}
          </div>
        </div>
        <div className="mt-3">
          <div className="font-mono text-2xl md:text-[26px] font-medium tracking-tight text-stone-900" data-testid={`kpi-value-${metric.metric_id}`}>
            {formatValue(current, unit)}
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs">
            <span className={cn("inline-flex items-center gap-1 font-mono font-medium", color)} data-testid={`kpi-delta-${metric.metric_id}`}>
              <Icon className="w-3.5 h-3.5" />
              {unit === "percent"
                ? (delta_abs === null || delta_abs === undefined
                    ? "—"
                    : `${Math.abs(delta_abs).toFixed(1)} pts`)  // change in percentage points
                : (delta_pct === null || delta_pct === undefined
                    ? "—"
                    : `${Math.abs(delta_pct).toFixed(1)}%`)}
            </span>
            <span className="text-stone-500 font-mono">
              vs {formatValue(prior, unit)}
            </span>
          </div>
        </div>
        <div className="mt-4 h-12">
          {trendData.filter((t) => t.value !== null).length > 1 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                <YAxis hide domain={["auto", "auto"]} />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={isFlat ? "#8A8D8A" : "#3C4A3E"}
                  strokeWidth={1.5}
                  dot={false}
                  isAnimationActive
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-end">
              <div className="text-[10px] font-mono text-stone-400">insufficient history</div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
